/**
 * TrainerSubmissionResults Page
 *
 * Displays scoring results and transcript for a submission (artifact linked to assessment).
 * Route: /trainer/submissions/:submissionId
 *
 * Data Loading (on-demand per addendum):
 * 1. Load the source artifact (submission) via artifactId
 * 2. Find artifact links with TRANSCRIBED_FROM to locate transcript artifacts
 * 3. Use transcript artifact version ID to fetch scoring jobs
 * 4. Display results or appropriate "pending" states
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../lib/api';

import { ScoreDisplay } from '../../components/ScoreDisplay';
import { CompetencyScoreTable } from '../../components/CompetencyScoreTable';
import { Code } from '../../components/JsonViewer';
import type { ArtifactWithVersions, ScoringJob } from '../../lib/types';

type PageState = 'loading' | 'ready' | 'error' | 'no_transcript' | 'scoring_pending';

interface TranscriptContent {
  text: string;
  turns?: Array<{
    role: string;
    text: string;
    timestamp?: string;
  }>;
}

export function TrainerSubmissionResults() {
  const { submissionId } = useParams<{ submissionId: string }>();
  const [searchParams] = useSearchParams();
  const assessmentId = searchParams.get('assessmentId');
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Loaded data
  const [sourceArtifact, setSourceArtifact] = useState<ArtifactWithVersions | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_transcriptArtifact, setTranscriptArtifact] = useState<ArtifactWithVersions | null>(null);
  const [scoringJob, setScoringJob] = useState<ScoringJob | null>(null);
  const [transcriptContent, setTranscriptContent] = useState<TranscriptContent | null>(null);

  // Transcript search filter
  const [transcriptFilter, setTranscriptFilter] = useState('');

  const loadData = useCallback(async () => {
    if (!submissionId) return;

    setPageState('loading');
    setErrorMessage(null);

    try {
      // Step 1: Load the source artifact (the submission = audio artifact)
      const artifactRes = await apiClient.getArtifact(submissionId);
      if (artifactRes.response.error || !artifactRes.response.data) {
        throw new Error(artifactRes.response.error?.message ?? 'Failed to load submission artifact');
      }
      setSourceArtifact(artifactRes.response.data);

      const sourceArtifactData = artifactRes.response.data;
      let transcriptVersionId: string | null = null;
      let loadedTranscript: ArtifactWithVersions | null = null;

      // Check if the source artifact itself is a transcript (application/json with conversation data)
      if (sourceArtifactData.mimeType === 'application/json') {
        // This might be a transcript artifact directly (conversation type assessment)
        loadedTranscript = sourceArtifactData;
        transcriptVersionId = sourceArtifactData.versions[0]?.id ?? null;
      } else {
        // Step 2: Source is audio - look up transcripts by the audio version ID
        // This correctly binds to THIS specific submission, not just any transcript for the assessment
        const audioVersionId = sourceArtifactData.versions[0]?.id;
        if (!audioVersionId) {
          setPageState('no_transcript');
          return;
        }

        const transcriptsRes = await apiClient.getTranscriptsByAudio(audioVersionId);
        if (transcriptsRes.response.data && transcriptsRes.response.data.items.length > 0) {
          // Find completed transcription job
          const completedTranscriptionJobs = transcriptsRes.response.data.items.filter(
            (job) => job.status === 'completed' && (job.textArtifactVersionId || job.jsonArtifactVersionId)
          );

          if (completedTranscriptionJobs.length > 0) {
            // Sort by createdAt descending to get most recent
            completedTranscriptionJobs.sort((a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );

            const transcriptionJob = completedTranscriptionJobs[0];
            if (transcriptionJob) {
              // Prefer JSON transcript artifact for structured turns, fall back to text
              transcriptVersionId = transcriptionJob.jsonArtifactVersionId ?? transcriptionJob.textArtifactVersionId ?? null;
              const transcriptArtifactId = transcriptionJob.jsonArtifactId ?? transcriptionJob.textArtifactId;

              if (transcriptArtifactId) {
                const transcriptArtifactRes = await apiClient.getArtifact(transcriptArtifactId);
                if (transcriptArtifactRes.response.data) {
                  loadedTranscript = transcriptArtifactRes.response.data;
                }
              }
            }
          } else {
            // Transcription jobs exist but none completed - still processing
            setPageState('no_transcript');
            return;
          }
        }
      }

      if (!transcriptVersionId) {
        // No transcript found yet - may still be processing
        setPageState('no_transcript');
        return;
      }

      setTranscriptArtifact(loadedTranscript);

      // Step 3: Load transcript content
      if (loadedTranscript) {
        const contentRes = await apiClient.getArtifactContentAsText(loadedTranscript.id);
        if (contentRes.response.data) {
          try {
            const parsed = JSON.parse(contentRes.response.data);
            // Handle both transcript artifact schema and plain text
            if (parsed.turns) {
              setTranscriptContent({ text: parsed.turns.map((t: { text: string }) => t.text).join('\n\n'), turns: parsed.turns });
            } else if (parsed.text) {
              setTranscriptContent({ text: parsed.text });
            } else {
              setTranscriptContent({ text: contentRes.response.data });
            }
          } catch {
            // Plain text transcript
            setTranscriptContent({ text: contentRes.response.data });
          }
        }
      }

      // Step 4: Load scoring job for this specific transcript
      const scoresRes = await apiClient.getScoresByTranscript(transcriptVersionId);
      if (scoresRes.response.data && scoresRes.response.data.items.length > 0) {
        // Get the most recent completed scoring job
        const completedJobs = scoresRes.response.data.items.filter(
          (job) => job.status === 'completed' && job.result
        );

        if (completedJobs.length > 0) {
          // Sort by createdAt descending
          completedJobs.sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          setScoringJob(completedJobs[0] ?? null);
          setPageState('ready');
        } else {
          // Scoring jobs exist but none completed
          const pendingJobs = scoresRes.response.data.items.filter(
            (job) => job.status === 'pending'
          );
          if (pendingJobs.length > 0) {
            setPageState('scoring_pending');
          } else {
            // All jobs failed
            setPageState('ready');
            setScoringJob(scoresRes.response.data.items[0] ?? null);
          }
        }
      } else {
        // No scoring job yet
        setPageState('scoring_pending');
      }
    } catch (err) {
      console.error('[TrainerSubmissionResults] Load error:', err);
      setErrorMessage(err instanceof Error ? err.message : 'Failed to load results');
      setPageState('error');
    }
  }, [submissionId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter transcript content for search
  const filteredTranscript = transcriptContent?.turns
    ? transcriptContent.turns.filter(
        (turn) =>
          !transcriptFilter ||
          turn.text.toLowerCase().includes(transcriptFilter.toLowerCase()) ||
          turn.role.toLowerCase().includes(transcriptFilter.toLowerCase())
      )
    : null;

  return (
    <div>
      {/* Back link */}
      <div className="flex items-center gap-4 mb-4">
        {assessmentId ? (
          <Link to={`/console/trainer/assessments/${assessmentId}`} style={{ color: 'var(--text-tertiary)' }}>
            &larr; Back to Assessment
          </Link>
        ) : (
          <Link to="/console/trainer/assessments" style={{ color: 'var(--text-tertiary)' }}>
            &larr; Back to Assessments
          </Link>
        )}
      </div>

      <h1>Submission Results</h1>

      {/* Loading state */}
      {pageState === 'loading' && (
        <div className="card">
          <p className="text-muted">Loading results...</p>
        </div>
      )}

      {/* Error state */}
      {pageState === 'error' && (
        <div className="error-box">
          {errorMessage ?? 'Failed to load results'}
          <div style={{ marginTop: 12 }}>
            <button className="btn btn-secondary" onClick={loadData}>
              Retry
            </button>
          </div>
        </div>
      )}

      {/* No transcript yet */}
      {pageState === 'no_transcript' && (
        <div className="card">
          <div
            style={{
              padding: '24px',
              textAlign: 'center',
              color: 'var(--text-secondary)',
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
            <h3 style={{ margin: '0 0 8px 0' }}>Transcript Not Ready</h3>
            <p className="text-muted">
              The transcript is still being processed. Please check back in a few moments.
            </p>
            <button
              className="btn btn-secondary"
              onClick={loadData}
              style={{ marginTop: 16 }}
            >
              Refresh
            </button>
          </div>
        </div>
      )}

      {/* Scoring pending */}
      {pageState === 'scoring_pending' && (
        <>
          {/* Submission metadata */}
          {sourceArtifact && (
            <div className="card">
              <h2>Submission</h2>
              <table className="table" style={{ maxWidth: 500 }}>
                <tbody>
                  <tr>
                    <td style={{ fontWeight: 500, width: 150 }}>Artifact ID</td>
                    <td><Code>{sourceArtifact.id}</Code></td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 500 }}>Type</td>
                    <td>{sourceArtifact.mimeType}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 500 }}>Submitted</td>
                    <td>{new Date(sourceArtifact.createdAt).toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <div className="card">
            <div
              style={{
                padding: '24px',
                textAlign: 'center',
                color: 'var(--text-secondary)',
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
              <h3 style={{ margin: '0 0 8px 0' }}>Scoring In Progress</h3>
              <p className="text-muted">
                The submission has been transcribed but scoring is still in progress.
              </p>
              <button
                className="btn btn-secondary"
                onClick={loadData}
                style={{ marginTop: 16 }}
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Show transcript if available */}
          {transcriptContent && (
            <div className="card">
              <h2>Transcript</h2>
              <TranscriptDisplay
                content={transcriptContent}
                filter={transcriptFilter}
                onFilterChange={setTranscriptFilter}
                filteredTurns={filteredTranscript}
              />
            </div>
          )}
        </>
      )}

      {/* Ready - show full results */}
      {pageState === 'ready' && (
        <>
          {/* Submission metadata */}
          {sourceArtifact && (
            <div className="card">
              <h2>Submission</h2>
              <table className="table" style={{ maxWidth: 500 }}>
                <tbody>
                  <tr>
                    <td style={{ fontWeight: 500, width: 150 }}>Artifact ID</td>
                    <td><Code>{sourceArtifact.id}</Code></td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 500 }}>Type</td>
                    <td>{sourceArtifact.mimeType}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 500 }}>Submitted</td>
                    <td>{new Date(sourceArtifact.createdAt).toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Overall Score Card */}
          {scoringJob?.result ? (
            <div className="card">
              <h2>Overall Score</h2>
              <div style={{ marginBottom: 16 }}>
                <ScoreDisplay
                  overallScore={scoringJob.result.overallScore}
                  maxPossibleScore={scoringJob.result.maxPossibleScore}
                  normalizedScore={scoringJob.result.normalizedScore}
                  passed={scoringJob.result.passed}
                  size="large"
                />
              </div>

              <table className="table" style={{ maxWidth: 400 }}>
                <tbody>
                  <tr>
                    <td style={{ fontWeight: 500 }}>Scoring Job</td>
                    <td><Code>{scoringJob.id.slice(0, 12)}...</Code></td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 500 }}>Rubric</td>
                    <td>
                      <Link to={`/console/trainer/rubrics/${scoringJob.rubricId}`}>
                        <Code>{scoringJob.rubricId.slice(0, 12)}...</Code>
                      </Link>
                      {' '}v{scoringJob.rubricVersion}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 500 }}>Execution Time</td>
                    <td>{scoringJob.result.executionTimeMs}ms</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 500 }}>Scored At</td>
                    <td>{new Date(scoringJob.result.executedAt).toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : scoringJob?.status === 'failed' ? (
            <div className="card">
              <h2>Scoring Failed</h2>
              <div className="error-box">
                {scoringJob.errorMessage ?? 'Scoring failed. Please try again.'}
              </div>
            </div>
          ) : null}

          {/* Competency Breakdown */}
          {scoringJob?.result && (
            <div className="card">
              <h2>Competency Breakdown</h2>
              <CompetencyScoreTable
                dimensionScores={scoringJob.result.dimensionScores}
                showConfidence={true}
              />
            </div>
          )}

          {/* Transcript */}
          {transcriptContent && (
            <div className="card">
              <h2>Transcript</h2>
              <TranscriptDisplay
                content={transcriptContent}
                filter={transcriptFilter}
                onFilterChange={setTranscriptFilter}
                filteredTurns={filteredTranscript}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Helper component for transcript display */
function TranscriptDisplay({
  content,
  filter,
  onFilterChange,
  filteredTurns,
}: {
  content: TranscriptContent;
  filter: string;
  onFilterChange: (value: string) => void;
  filteredTurns: Array<{ role: string; text: string; timestamp?: string }> | null;
}) {
  return (
    <div>
      {/* Search filter */}
      <div style={{ marginBottom: 12 }}>
        <input
          type="text"
          className="input"
          placeholder="Search transcript..."
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
          style={{ maxWidth: 300 }}
        />
      </div>

      {/* Transcript content */}
      {filteredTurns ? (
        <div
          style={{
            maxHeight: 500,
            overflow: 'auto',
            border: '1px solid var(--border-default)',
            borderRadius: 4,
          }}
        >
          {filteredTurns.length === 0 ? (
            <p className="text-muted" style={{ padding: 16 }}>
              No matching content found.
            </p>
          ) : (
            filteredTurns.map((turn, i) => (
              <div
                key={i}
                style={{
                  padding: '12px 16px',
                  borderBottom: i < filteredTurns.length - 1 ? '1px solid var(--border-default)' : 'none',
                  background: turn.role === 'user' ? 'var(--bg-muted)' : 'transparent',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      color: turn.role === 'user' ? 'var(--semantic-info, #3b82f6)' : 'var(--semantic-success, #22c55e)',
                    }}
                  >
                    {turn.role}
                  </span>
                  {turn.timestamp && (
                    <span className="text-muted mono" style={{ fontSize: 10 }}>
                      {new Date(turn.timestamp).toLocaleTimeString()}
                    </span>
                  )}
                </div>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                  {turn.text}
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div
          style={{
            maxHeight: 500,
            overflow: 'auto',
            padding: 16,
            border: '1px solid var(--border-default)',
            borderRadius: 4,
            whiteSpace: 'pre-wrap',
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            lineHeight: 1.6,
            background: 'var(--bg-muted)',
          }}
        >
          {filter
            ? content.text.split('\n').filter((line) =>
                line.toLowerCase().includes(filter.toLowerCase())
              ).join('\n') || 'No matching content found.'
            : content.text}
        </div>
      )}
    </div>
  );
}
